// Home — nearby games list (UC-04) with a filter bar (UC-19).

import $ from 'jquery';
import type { GameFilter } from '@shared/dto';
import type { GameFormat, SkillLevel } from '@shared/models';
import type { Screen } from './types';
import { api } from '../api';
import { getCurrentUser } from '../session';
import { gameCardHtml } from '../ui/gameCard';
import { emptyHtml, errorHtml, loadingHtml } from '../ui/states';
import { FORMAT_TR, SKILL_TR } from '../ui/format';
import { esc } from '../ui/dom';

// Filter state persists across re-renders within a session.
const filter: GameFilter = {};

const FORMATS: GameFormat[] = ['3v3', '4v4', '5v5', 'pickup'];
const SKILLS: SkillLevel[] = ['beginner', 'intermediate', 'advanced'];

function filterBarHtml(): string {
  const formatChips = FORMATS.map((f) => {
    const on = filter.formats?.includes(f);
    return `<button class="chip ${on ? 'is-on' : ''}" data-format="${f}">${FORMAT_TR[f]}</button>`;
  }).join('');

  const skillOpts = ['<option value="">Tüm seviyeler</option>']
    .concat(SKILLS.map((s) => `<option value="${s}" ${filter.skill === s ? 'selected' : ''}>${SKILL_TR[s]}</option>`))
    .join('');

  const distOpts = [
    ['', 'Her mesafe'],
    ['3', '3 km'],
    ['7', '7 km'],
    ['15', '15 km'],
  ]
    .map(([v, l]) => `<option value="${v}" ${String(filter.maxDistanceKm ?? '') === v ? 'selected' : ''}>${l}</option>`)
    .join('');

  return `
  <div class="filterbar">
    <div class="filterbar__chips">${formatChips}</div>
    <div class="filterbar__selects">
      <select id="f-skill" class="select">${skillOpts}</select>
      <select id="f-dist" class="select">${distOpts}</select>
    </div>
  </div>`;
}

export const HomeScreen: Screen = async (body) => {
  const user = getCurrentUser();
  body.html(`
    <div class="home__greeting">Merhaba <b>${esc(user?.name.split(' ')[0] ?? '')}</b> 👋<br>
      <span>${esc(user?.homeArea ?? '')} çevresindeki oyunlar</span></div>
    <div id="my-games"></div>
    <div class="section-title">Keşfet · herkese açık oyunlar</div>
    ${filterBarHtml()}
    <div id="game-list">${loadingHtml()}</div>
  `);

  // "My games": everything I organize or joined — the only place private games
  // (which never appear in public discovery) are reachable from Home.
  async function loadMine(): Promise<void> {
    try {
      const mine = await api.call('games.mine', undefined);
      if (mine.length === 0) return;
      $('#my-games').html(
        `<div class="section-title">Oyunlarım</div>${mine.map(gameCardHtml).join('')}`,
      );
    } catch {
      /* non-fatal: the discovery list still renders */
    }
  }

  async function reload(): Promise<void> {
    const $list = $('#game-list');
    $list.html(loadingHtml());
    try {
      const games = await api.call('games.list', { filter });
      if (games.length === 0) {
        $list.html(emptyHtml('🔍', 'Filtreye uyan oyun yok. Filtreleri gevşet veya kendi oyununu oluştur.'));
        return;
      }
      $list.html(games.map(gameCardHtml).join(''));
    } catch (err) {
      $list.html(errorHtml(err instanceof Error ? err.message : String(err)));
    }
  }

  // Filter interactions
  body.on('click', '.chip[data-format]', function () {
    const f = $(this).data('format') as GameFormat;
    const set = new Set(filter.formats ?? []);
    if (set.has(f)) set.delete(f);
    else set.add(f);
    filter.formats = set.size ? [...set] : undefined;
    $(this).toggleClass('is-on');
    void reload();
  });
  body.on('change', '#f-skill', function () {
    const v = $(this).val() as string;
    filter.skill = (v || undefined) as SkillLevel | undefined;
    void reload();
  });
  body.on('change', '#f-dist', function () {
    const v = $(this).val() as string;
    filter.maxDistanceKm = v ? Number(v) : undefined;
    void reload();
  });

  await Promise.all([loadMine(), reload()]);
};
